import { useState, useEffect, useRef } from 'react';
import { Search, X, Check, UserPlus } from 'lucide-react';

/**
 * InlineCustomerPicker — iter407.
 *
 * Click-to-assign customer cell for the Sales table. Click → small
 * popover with a search input + filtered list of customers. Pick one
 * to fire onSave({customer_id, customer_name}). Esc / outside-click
 * cancels. "Clear" link removes the customer link.
 *
 * Designed to be lightweight (no Dialog overlay); the popover is a
 * relatively-positioned div anchored to the trigger button.
 */
export function InlineCustomerPicker({
  value = null,           // {id, name} or null
  customers = [],
  onSave,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? customers.filter((c) => (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(q)).slice(0, 30)
    : customers.slice(0, 30);

  const pick = async (c) => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave({ customer_id: c?.id || null, customer_name: c?.name || '' });
      setOpen(false);
      setQuery('');
    } catch { /* parent toasts */ }
    finally { setSaving(false); }
  };

  return (
    <span className="relative inline-block" ref={wrapperRef} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`text-left rounded px-1 -mx-1 transition-colors ${
          value?.name ? 'text-foreground hover:bg-info/10' : 'text-muted-foreground hover:text-info hover:bg-info/10'
        }`}
        data-testid={testid ? `${testid}-display` : undefined}
        title={value?.name ? 'Click to change customer' : 'Click to assign customer'}
      >
        {value?.name ? (
          <span className="text-xs truncate inline-flex items-center gap-1 max-w-[140px]">
            <span className="truncate">{value.name}</span>
          </span>
        ) : (
          <span className="text-[10px] inline-flex items-center gap-1 italic"><UserPlus size={10} /> Add customer</span>
        )}
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 left-0 w-64 rounded-lg border border-border bg-popover shadow-lg"
          data-testid={testid ? `${testid}-popover` : undefined}
        >
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border">
            <Search size={12} className="text-muted-foreground" />
            <input
              type="text"
              placeholder="Search customers…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              className="flex-1 bg-transparent border-none outline-none text-xs"
              data-testid={testid ? `${testid}-search` : undefined}
            />
            {value?.id && (
              <button
                type="button"
                onClick={() => pick(null)}
                className="text-[10px] text-error hover:text-error/80 px-1.5 py-0.5 rounded hover:bg-error/10"
                title="Clear customer"
                data-testid={testid ? `${testid}-clear` : undefined}
                disabled={saving}
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={() => { setOpen(false); setQuery(''); }}
              className="h-5 w-5 rounded hover:bg-muted flex items-center justify-center text-muted-foreground"
            >
              <X size={11} />
            </button>
          </div>
          <div className="max-h-[200px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-center text-[11px] text-muted-foreground">No customers match.</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pick(c)}
                  disabled={saving}
                  className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-info/10 transition-colors flex items-center justify-between gap-2 disabled:opacity-50 ${
                    value?.id === c.id ? 'bg-info/15 font-semibold' : ''
                  }`}
                  data-testid={testid ? `${testid}-option-${c.id}` : undefined}
                >
                  <span className="truncate flex-1">{c.name}</span>
                  {c.phone && <span className="text-[9px] text-muted-foreground shrink-0">{c.phone}</span>}
                  {value?.id === c.id && <Check size={11} className="text-info shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </span>
  );
}

export default InlineCustomerPicker;
